import React, { useState, useMemo } from 'react';
import Switch from '../Switch.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/store.js';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { logout } from '../../api/auth.js';
import { getDisplayPreference, updateDisplayPreference, updateAvatarName } from '../../api/user.js';
import { DeleteUserModal } from './modals/DeleteUserModal.jsx';
import { SuccessModal } from './modals/SuccessModal.jsx';
import '../../styles/Settings.css';
import { getRandomAvatarName } from '../avatar/avatarUserNames/avatarNamesGenerator.js';
import { GiPerspectiveDiceSixFacesRandom } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';

const Settings = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { user, setUser, setIsAuth } = useAuthStore(store => store);

    const [avatarName, setAvatarName] = useState('');
    const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [avatarNameErrorMessage, setAvatarNameErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [allowDisplay, setAllowDisplay] = useState('');
    const [showPWUpdateSuccessModal, setShowPWUpdateSuccessModal] = useState(false);
    const [showAvatarNameUpdateSuccessModal, setShowAvatarNameUpdateSuccessModal] = useState(false);

    const isPasswordUpdateEnabled = user?.providerData[0]?.providerId === 'password';

    const allowDisplayQuery = useQuery({
        queryKey: ['allowLeaderboardDisplay', user?.uid],
        queryFn: () => getDisplayPreference(user?.uid),
        enabled: !(user?.uid === undefined || user?.uid === null),
    });

    const allowDisplayMutation = useMutation(updateDisplayPreference, {
        onSuccess: () => {
            queryClient.invalidateQueries(['allowLeaderboardDisplay', user?.uid]);
        },
    });

    const handlePasswordFormChange = e => {
        setFormData(prevFormData => {
            return {
                ...prevFormData,
                [e.target.name]: e.target.value,
            };
        });
    };

    const closePasswordUpdateSuccessModal = () => {
        setShowPWUpdateSuccessModal(false);
        logout();
        setUser(null);
        setIsAuth(false);
        navigate('/');
    };

    const handleAvatarNameChange = async() => {
        const randomAvatarName = getRandomAvatarName();
        setAvatarName(randomAvatarName);
    };

    const handlePasswordChange = async(e) => {
        e.preventDefault();

        const credential = EmailAuthProvider.credential(
            user.email,
            formData.currentPassword,
        );

        try {
            await reauthenticateWithCredential(user, credential);

            if (formData.newPassword !== formData.confirmNewPassword) {
                setPasswordErrorMessage('Passwords do not match!');
                return;
            } else if (formData.newPassword === formData.currentPassword) {
                setPasswordErrorMessage('New password cannot be the same as current password!');
                return;
            }

            await updatePassword(user, formData.newPassword);
            setSuccessMessage('Password updated successfully! You will be logged out.');
            setShowPWUpdateSuccessModal(true);
        } catch (error) {
            if (error.code === 'auth/wrong-password') {
                setPasswordErrorMessage('The current password is incorrect.');
            } else if (error.code === 'auth/invalid-login-credentials') {
                setPasswordErrorMessage('The current password is incorrect.');
            } else {
                setPasswordErrorMessage(error.code);
            }
        }
    };

    const handleUpdateAvatarName = async(e) => {
        e.preventDefault();
        if (avatarName === '') {
            return setAvatarNameErrorMessage('Please click the button above to generate a user name for you!');
        }
        try {
            await updateAvatarName(avatarName, user?.uid);
            queryClient.invalidateQueries(['avatarName', user?.uid]);
            setSuccessMessage('Your avatar name has been updated successfully!');
            setShowAvatarNameUpdateSuccessModal(true);
        } catch (e) {
            setAvatarNameErrorMessage('There has been an error updating your avatar name. Please try again.');
        }
    };

    const handleUpdateAllowDisplay = async(e) => {
        allowDisplayMutation.mutate({ isToggled: !allowDisplayQuery.data, uid: user?.uid });
        setTimeout(() => {
            navigate(0);
        }, 1000);
    };

    useMemo(() => {
        // update the allowDisplay variable.
        if (allowDisplayQuery.data !== undefined) {
            setAllowDisplay(allowDisplayQuery.data);
        };
    }, [allowDisplayQuery.isFetched, allowDisplayQuery.data]);

    return (
        <div className='profile-setting flex-grow-1 h-100 d-flex'>
            <div className="container-fluid h-100">
                <div className='row'>
                    <div className='col-12'>
                        <div className='leaderboard-share justify-content-between'>
                            <h3>Share my achievements on the Leaderboard</h3>
                            <Switch isToggled={allowDisplay} onToggle={handleUpdateAllowDisplay} />
                        </div>
                    </div>
                </div>

                {isPasswordUpdateEnabled && (
                    <div className='row'>
                        <div className='col-12'>
                            <div className='card'>
                                <div className="card-title">
                                    <h2 className="setting-pwd-title">Change your password</h2>
                                    <h3 className="setting-pwd-note-line">Enter a new password below to change your password</h3>
                                </div>
                                <form className="setting-pwd-form d-flex flex-column" onSubmit={handlePasswordChange}>
                                    <div className="col-12 col-md-5">
                                        <input
                                            type='password'
                                            placeholder='Current Password'
                                            name='currentPassword'
                                            className="setting-pwd-form-control"
                                            onChange={handlePasswordFormChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-12 col-md-5">
                                        <input
                                            type='password'
                                            placeholder='New Password'
                                            name='newPassword'
                                            className="setting-pwd-form-control"
                                            onChange={handlePasswordFormChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-12 col-md-5">
                                        <input
                                            type='password'
                                            placeholder='Confirm New Password'
                                            name='confirmNewPassword'
                                            className="setting-pwd-form-control"
                                            onChange={handlePasswordFormChange}
                                            required
                                        />
                                    </div>
                                    {passwordErrorMessage && <p className='settings-error'>{passwordErrorMessage}</p>}
                                    <button type="submit" className="setting-pwd-btn">Confirm</button>
                                </form>
                            </div>
                        </div>
                    </div>
                )
                }

                <div className='row'>
                    <div className='col-12'>
                        <div className='card'>
                            <div className="card-title">
                                <h2 className="setting-name-title">Change your avatar name</h2>
                                <h3 className="setting-name-note-line">Click the refresh button to generate a random avatar name</h3>
                            </div>
                            <form className="setting-name-form d-flex flex-column">
                                <div className="d-flex col-12 col-md-6">
                                    <input
                                        type='name'
                                        placeholder='New Avatar Name'
                                        name='newAvatarName'
                                        value={avatarName}
                                        required
                                        className="setting-name-form-control"
                                        readOnly
                                    />
                                    <div className='rounded-4xl'>
                                        <GiPerspectiveDiceSixFacesRandom
                                            className='random-dice'
                                            size='2rem'
                                            style={{
                                                margin: '0.7rem 3rem 0.7rem 1rem',
                                                color: 'red',
                                            }} onClick={() => handleAvatarNameChange()}>
                                        </GiPerspectiveDiceSixFacesRandom>
                                    </div>
                                </div>
                                {avatarNameErrorMessage && <p className='settings-name-error'>{avatarNameErrorMessage}</p>}
                                <button type="submit" className="setting-name-btn" onClick={(e) => handleUpdateAvatarName(e)}>Update</button>
                            </form>
                        </div>
                    </div>
                </div>
                {
                    showPWUpdateSuccessModal && (
                        <SuccessModal
                            showSuccessModal={showPWUpdateSuccessModal}
                            hideSuccessModal={() => setShowPWUpdateSuccessModal(false)}
                            onConfirm={closePasswordUpdateSuccessModal}
                            successMessage={successMessage}
                        />
                    )
                }
                {
                    showAvatarNameUpdateSuccessModal && (
                        <SuccessModal
                            showSuccessModal={showAvatarNameUpdateSuccessModal}
                            hideSuccessModal={() => setShowAvatarNameUpdateSuccessModal(false)}
                            onConfirm={() => setShowAvatarNameUpdateSuccessModal(false)}
                            successMessage={successMessage}
                        />
                    )
                }
                <DeleteUserModal />
            </div>
        </div>
    );
};

export default Settings;
